import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import {
  DataGridPremium,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridPaginationModel,
} from "@mui/x-data-grid-premium";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { DataGridManageColumnsPanel } from "../../../components/shared/DataGridManageColumnsPanel";
import {
  aiPlanDataGridPanelSx,
  getDataGridEmptyStateSx,
  getRegulationsListDataGridSx,
  aiHubListDataGridRowBorderSx,
} from "../../../components/shared/regulationsListDataGridSx";
import styles from "../../../components/RegulationsTable/RegulationsTable.module.css";
import noDataAnim from "../../../assets/no-data-anim.webp";
import {
  getAiHubStatusChipSx,
  type AiHubStatusTone,
} from "../aiHubChipStyles";
import { buildAiHubObjectAnalysisPath } from "../constants";
import AiHubTableToolbar from "../AiHubTableToolbar";
import {
  type ObjectAnalysisPairRow,
  type ObjectAnalysisRelevance,
} from "./objectAnalysisData";

type ObjectAnalysisPairTableProps = Readonly<{
  rows: ObjectAnalysisPairRow[];
  rowCount: number;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
}>;

export const RELEVANCE_TONES: Record<
  ObjectAnalysisRelevance,
  AiHubStatusTone
> = {
  High: "green",
  Medium: "yellow",
  Low: "red",
};

function NoRowsOverlay() {
  return (
    <Box
      sx={{
        height: "100%",
        minHeight: 280,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "background.paper",
      }}
    >
      <Box component="img" src={noDataAnim} alt="" sx={{ width: 160, mb: 2 }} />
      <Typography sx={{ color: "#8c8c8c", fontSize: 14 }}>
        No data available.
      </Typography>
    </Box>
  );
}

export function MatchingTagsCell({ tags }: { tags: string[] }) {
  if (tags.length === 0) {
    return <Box sx={{ color: "#8c8c8c" }}>-</Box>;
  }
  return (
    <Box
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        alignContent: "center",
        gap: "4px",
        height: "100%",
        py: "8px",
      }}
    >
      {tags.map((tag) => (
        <Chip
          key={tag}
          size="small"
          label={tag}
          sx={getAiHubStatusChipSx("blue")}
        />
      ))}
    </Box>
  );
}

function DetailsLine({
  text,
  color,
  fontSize,
}: {
  text: string;
  color: string;
  fontSize: number;
}) {
  return (
    <Typography
      noWrap
      title={text}
      sx={{
        fontSize,
        color,
        lineHeight: 1.3,
        width: "100%",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {text}
    </Typography>
  );
}

function DetailsCell({ title, detail }: { title: string; detail: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        gap: "2px",
        height: "100%",
        width: "100%",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <DetailsLine text={title} color="#262626" fontSize={13} />
      <DetailsLine text={detail} color="#8c8c8c" fontSize={12} />
    </Box>
  );
}

export default function ObjectAnalysisPairTable({
  rows,
  rowCount,
  paginationModel,
  onPaginationModelChange,
}: ObjectAnalysisPairTableProps) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>({});
  const panelScrollTopRef = useRef(0);

  const columnsManagementSlot = useCallback(
    () => (
      <DataGridManageColumnsPanel
        columnVisibilityModel={columnVisibilityModel}
        setColumnVisibilityModel={setColumnVisibilityModel}
        scrollTopRef={panelScrollTopRef}
      />
    ),
    [columnVisibilityModel],
  );

  const toolbarSlot = useCallback(
    () => <AiHubTableToolbar showExport={false} />,
    [],
  );

  const columns = useMemo<GridColDef<ObjectAnalysisPairRow>[]>(
    () => [
      {
        field: "assessmentId",
        headerName: "Result ID",
        width: 140,
        sortable: false,
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            <Link
              component="button"
              type="button"
              underline="none"
              sx={{ fontSize: 13 }}
              onClick={() =>
                navigate(buildAiHubObjectAnalysisPath(row.assessmentId))
              }
            >
              {row.assessmentId}
            </Link>
          </Box>
        ),
      },
      {
        field: "objectAType",
        headerName: "Object A",
        width: 100,
        sortable: false,
      },
      {
        field: "objectATitle",
        headerName: "Object A Details",
        flex: 1,
        minWidth: 230,
        sortable: false,
        cellClassName: "o2o-details-cell",
        renderCell: ({ row }) => (
          <DetailsCell title={row.objectATitle} detail={row.objectADetails} />
        ),
      },
      {
        field: "objectBType",
        headerName: "Object B",
        width: 110,
        sortable: false,
      },
      {
        field: "objectBTitle",
        headerName: "Object B Details",
        flex: 1,
        minWidth: 210,
        sortable: false,
        cellClassName: "o2o-details-cell",
        renderCell: ({ row }) => (
          <DetailsCell title={row.objectBTitle} detail={row.objectBDetails} />
        ),
      },
      {
        field: "matchingTags",
        headerName: "Matching Tags",
        flex: 1.2,
        minWidth: 240,
        sortable: false,
        valueGetter: (_value, row) => row.matchingTags.join(", "),
        renderCell: ({ row }) => <MatchingTagsCell tags={row.matchingTags} />,
      },
      {
        field: "score",
        headerName: "Score",
        width: 90,
        sortable: false,
      },
      {
        field: "relevance",
        headerName: "Relevance",
        width: 120,
        sortable: false,
        type: "singleSelect",
        valueOptions: ["High", "Medium", "Low"],
        renderCell: ({ row }) => (
          <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
            {row.relevance ? (
              <Chip
                size="small"
                label={row.relevance}
                sx={getAiHubStatusChipSx(RELEVANCE_TONES[row.relevance])}
              />
            ) : (
              <Typography sx={{ fontSize: 13, color: "#8c8c8c" }}>-</Typography>
            )}
          </Box>
        ),
      },
    ],
    [navigate],
  );

  const isEmpty = rows.length === 0;

  return (
    <Box
      className={`${styles.dataGridPanel}${
        isEmpty ? ` ${styles.dataGridPanelEmpty}` : ""
      }`}
      sx={{
        ...aiPlanDataGridPanelSx,
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minHeight: 480,
      }}
    >
      <DataGridPremium
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        rowHeight={72}
        disableRowSelectionOnClick
        disableRowGrouping
        disableAggregation
        disablePivoting
        disableColumnSorting
        disableColumnFilter
        pagination
        paginationMode="server"
        rowCount={rowCount}
        paginationModel={paginationModel}
        onPaginationModelChange={onPaginationModelChange}
        pageSizeOptions={[5, 10, 20, 50]}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={setColumnVisibilityModel}
        showToolbar
        showColumnVerticalBorder={false}
        pinnedColumnsSectionSeparator="border"
        slots={{
          toolbar: toolbarSlot,
          noRowsOverlay: NoRowsOverlay,
          columnsManagement: columnsManagementSlot,
        }}
        sx={
          [
            getRegulationsListDataGridSx(theme.palette.primary.main),
            aiHubListDataGridRowBorderSx,
            ...(isEmpty ? [getDataGridEmptyStateSx()] : []),
            {
              minHeight: 400,
              "& .MuiDataGrid-toolbarQuickFilter": { display: "none" },
              "& .o2o-details-cell": {
                overflow: "hidden",
              },
              "& .o2o-details-cell .MuiDataGrid-cellContent": {
                width: "100%",
                minWidth: 0,
                overflow: "hidden",
              },
            },
          ] as SxProps<Theme>
        }
      />
    </Box>
  );
}
